/**
 * Regra de força de senha — fonte ÚNICA do frontend (at-68).
 *
 * Estava copiada em `usuarios.tsx`, `criarUsuario.tsx` e no `ModalCriarUsuario` (órfão,
 * apagado na at-67), e **ausente** no `ModalPrimeiroAcesso`, que valida só "preenchido e
 * iguais".
 *
 * 🔴 O backend tem DOIS regex diferentes, e é por isso que o conjunto abaixo é o menor:
 *   - `SignupDto` (`auth.dto.ts`) aceita `@ $ ! % * ? &`;
 *   - `AuthService.isPasswordStrong` aceita esses MAIS `# ^ _ -`.
 * O caminho de criação passa pelos dois (o DTO primeiro), então uma senha com `_` passa
 * no service e é recusada com 400 pelo DTO. Usar a INTERSEÇÃO faz toda senha aceita aqui
 * valer nos dois caminhos — criação e senha temporária. A divergência do backend está
 * registrada em `.claude/12-bugs.md`.
 */

/** Os especiais que o backend aceita nos DOIS caminhos. */
export const ESPECIAIS_ACEITOS = '@$!%*?&';

/** Texto da regra, para a dica do campo e a mensagem de erro dizerem a mesma coisa. */
export const REGRA_SENHA =
  'Mínimo de 8 caracteres, com ao menos uma letra, um número e um caractere especial (@ $ ! % * ? &).';

const REGEX_SENHA_FORTE = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

export function senhaForte(senha: string): boolean {
  return REGEX_SENHA_FORTE.test(senha ?? '');
}

const LETRAS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITOS = '0123456789';
const TODOS = LETRAS + DIGITOS + ESPECIAIS_ACEITOS;

/**
 * Sorteia um índice com `crypto.getRandomValues` quando disponível. `Math.random` é o
 * fallback (jsdom antigo, navegador sem crypto) — não é criptograficamente forte, mas
 * uma senha temporária que o usuário troca no primeiro acesso tolera isso.
 */
function sortear(alfabeto: string): string {
  const cripto = typeof crypto !== 'undefined' ? crypto : undefined;

  if (cripto?.getRandomValues) {
    const buffer = new Uint32Array(1);
    cripto.getRandomValues(buffer);
    return alfabeto[buffer[0] % alfabeto.length];
  }

  return alfabeto[Math.floor(Math.random() * alfabeto.length)];
}

/**
 * Gera uma senha que passa em `senhaForte` por construção: garante uma letra, um dígito
 * e um especial, completa o tamanho e embaralha (senão os três primeiros caracteres
 * seriam sempre do mesmo tipo).
 */
export function gerarSenhaForte(tamanho = 12): string {
  const alvo = Math.max(8, tamanho);
  const caracteres = [sortear(LETRAS), sortear(DIGITOS), sortear(ESPECIAIS_ACEITOS)];

  while (caracteres.length < alvo) {
    caracteres.push(sortear(TODOS));
  }

  for (let i = caracteres.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [caracteres[i], caracteres[j]] = [caracteres[j], caracteres[i]];
  }

  return caracteres.join('');
}
